import React, { memo, useEffect, useRef, useState } from 'react';
import ModalWrapper from '../firstThreeModal/ModalWrapper';
import { AIAdvisorIntroContent } from '../firstThreeModal/AIAdvisorIntro/AIAdvisorIntro';
import { TrackFinancesContent } from '../firstThreeModal/TrackFinances/TrackFinances';
import { YourGoalIntroContent } from '../firstThreeModal/YourGoal/YourGoalIntro';
import { completeOnboarding } from '../../modules/dashbord/api/index';

const OnboardingModal = ({ userInfo, fetchMe, onComplete }) => {
	const [currentModal, setCurrentModal] = useState(0);
	const previousUserInfo = useRef(null);

	// Check if onboarding should be shown when userInfo changes (after login)
	useEffect(() => {
		const prevUserInfo = previousUserInfo.current;
		previousUserInfo.current = userInfo;

		if (!userInfo) {
			// User logged out - clear sessionStorage
			sessionStorage.removeItem('onboardingEvaluatedUserKey');
			setCurrentModal(0);
			return;
		}

		const userKey = userInfo.id || userInfo.email;

		if (!userKey) {
			return; // No valid user identifier
		}

		// Check if we've already evaluated this userKey (persists across remounts)
		const evaluatedUserKey = sessionStorage.getItem(
			'onboardingEvaluatedUserKey',
		);

		// If we've already evaluated this userKey, don't re-evaluate
		// This handles profile updates and component remounts
		if (userKey === evaluatedUserKey) {
			// Already evaluated - don't change modal state
			return;
		}

		// Detect if this is a login (userInfo went from null to set) vs profile update
		const wasNull = prevUserInfo === null;
		const isLogin = wasNull && userInfo !== null;

		// Only evaluate onboarding on login (userInfo went from null to set)
		// This prevents showing on profile updates (userInfo was already set)
		if (!isLogin) {
			// Not a login - this is a profile update or component remount with userInfo already set
			return;
		}

		// New login - evaluate onboarding and store in sessionStorage
		sessionStorage.setItem('onboardingEvaluatedUserKey', userKey);

		const shouldShow =
			userInfo.onboarding_version === null || userInfo.onboarding_version < 1;

		if (shouldShow) {
			setCurrentModal(1);
		} else {
			setCurrentModal(0);
		}
	}, [userInfo]);

	const handleCloseAll = () => {
		setCurrentModal(0);
		// Modal closed - userKey already in evaluatedUserKeys, so won't show again
		if (onComplete) {
			onComplete();
		}
	};

	const handleNext = () => {
		setCurrentModal(prev => prev + 1);
	};

	const handleFinish = async () => {
		try {
			await completeOnboarding(1);
			// Refresh user info to get updated onboarding_version
			if (fetchMe) {
				await fetchMe();
			}
			setCurrentModal(0);
			// Onboarding completed
			if (onComplete) {
				onComplete();
			}
		} catch (error) {
			console.error('Failed to complete onboarding:', error);
			// Still close the modal even if API call fails
			setCurrentModal(0);
			if (onComplete) {
				onComplete();
			}
		}
	};

	// Don't render anything if no modal should be shown
	if (currentModal === 0) {
		return null;
	}

	// Single persistent modal shell: overlay stays mounted so the page doesn't flash between stages
	return (
		<ModalWrapper isOpen onSkip={handleCloseAll}>
			{currentModal === 1 && (
				<AIAdvisorIntroContent
					onContinue={handleNext}
					onSkip={handleCloseAll}
				/>
			)}
			{currentModal === 2 && (
				<TrackFinancesContent onContinue={handleNext} onSkip={handleCloseAll} />
			)}
			{currentModal === 3 && (
				<YourGoalIntroContent onFinish={handleFinish} onSkip={handleCloseAll} />
			)}
		</ModalWrapper>
	);
};

export default memo(OnboardingModal);
